const RECOVERY_VERSION = 1;
const RECOVERY_DATABASE_VERSION = 2;
const RECOVERY_DATABASE_PREFIX = 'mindmappi-recovery-v1';
const RECOVERY_STORE = 'recoveries';
const PROPOSAL_STORE = 'pending-node-proposals';
export const MAX_RECOVERIES_PER_MAP = 3;
export const MAX_CONFLICTED_NODES = 25;
export const MAX_RECOVERY_BYTES = 25 * 1024 * 1024;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value) {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}

function nowIso(now) {
  const value = typeof now === 'function' ? now() : now;
  return (value instanceof Date ? value : new Date(value || Date.now())).toISOString();
}

function safeSnapshot(snapshot) {
  return {
    nodes: Array.isArray(snapshot?.nodes) ? snapshot.nodes : [],
    relationships: Array.isArray(snapshot?.relationships) ? snapshot.relationships : [],
    root: snapshot?.root || null,
    meta: snapshot?.meta && typeof snapshot.meta === 'object' ? snapshot.meta : {},
  };
}

function stableValue(value) {
  if (Array.isArray(value)) return value.map(stableValue);
  if (!value || typeof value !== 'object') return value;
  return Object.keys(value)
    .sort()
    .reduce((result, key) => {
      result[key] = stableValue(value[key]);
      return result;
    }, {});
}

function valuesMatch(left, right) {
  return JSON.stringify(stableValue(left)) === JSON.stringify(stableValue(right));
}

function finalizeRecovery(record) {
  const serialized = JSON.stringify(record);
  const byteLength = serializedByteLength(serialized);
  return {
    ...record,
    byteLength,
    storageEligible: byteLength <= MAX_RECOVERY_BYTES,
  };
}

function serializedByteLength(serialized) {
  return typeof TextEncoder === 'function'
    ? new TextEncoder().encode(serialized).byteLength
    : new Blob([serialized]).size;
}

export function buildOfflineRecoveryDatabaseName(userId) {
  return isUuid(userId) ? `${RECOVERY_DATABASE_PREFIX}:${userId}` : null;
}

export function buildRejectedAccessRecovery({ userId, mindmapId, snapshot, now = Date.now }) {
  if (!isUuid(userId) || !isUuid(mindmapId)) {
    throw new Error('A recovery copy requires an account and map');
  }
  const createdAt = nowIso(now);
  return finalizeRecovery({
    v: RECOVERY_VERSION,
    id: `${mindmapId}:access-rejected:${createdAt}`,
    userId,
    mindmapId,
    kind: 'access-rejected',
    createdAt,
    snapshot: safeSnapshot(snapshot),
    conflicts: [],
  });
}

export function findLostNodeProposals(proposals, currentNodes) {
  const currentById = currentNodes instanceof Map
    ? currentNodes
    : new Map((currentNodes || []).map((node) => [node.id, node]));
  return [...(proposals instanceof Map ? proposals : new Map()).entries()]
    .filter(([, proposal]) => !valuesMatch(currentById.get(proposal.id) || null, proposal))
    .slice(0, MAX_CONFLICTED_NODES)
    .map(([nodeId, localProposal]) => ({
      nodeId,
      localProposal,
      acceptedVersion: currentById.get(nodeId) || null,
    }));
}

export function isRejectedCollaborationClose(event) {
  return event?.code === 4403 && event?.reason === 'permission-denied';
}

export function buildConflictRecovery({
  userId,
  mindmapId,
  conflicts,
  snapshot,
  now = Date.now,
}) {
  if (!isUuid(userId) || !isUuid(mindmapId) || !Array.isArray(conflicts) || !conflicts.length) {
    throw new Error('A conflict recovery requires an account, map, and lost proposal');
  }
  const createdAt = nowIso(now);
  return finalizeRecovery({
    v: RECOVERY_VERSION,
    id: `${mindmapId}:whole-node-conflict:${createdAt}`,
    userId,
    mindmapId,
    kind: 'whole-node-conflict',
    createdAt,
    snapshot: safeSnapshot(snapshot),
    conflicts: conflicts.slice(0, MAX_CONFLICTED_NODES),
  });
}

function openRecoveryDatabase(userId, indexedDBFactory) {
  const databaseName = buildOfflineRecoveryDatabaseName(userId);
  if (!databaseName || !indexedDBFactory?.open) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const request = indexedDBFactory.open(databaseName, RECOVERY_DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(RECOVERY_STORE)) {
        database.createObjectStore(RECOVERY_STORE, { keyPath: 'id' });
      }
      if (!database.objectStoreNames.contains(PROPOSAL_STORE)) {
        database.createObjectStore(PROPOSAL_STORE, { keyPath: 'id' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Could not open recovery storage'));
  });
}

export async function saveOfflineNodeProposal(
  { userId, mindmapId, node, now = Date.now },
  indexedDBFactory = typeof window !== 'undefined' ? window.indexedDB : null
) {
  if (!isUuid(userId) || !isUuid(mindmapId) || !node?.id) return false;
  const proposal = {
    v: RECOVERY_VERSION,
    id: `${mindmapId}:${node.id}`,
    userId,
    mindmapId,
    nodeId: node.id,
    updatedAt: nowIso(now),
    node,
  };
  proposal.byteLength = serializedByteLength(JSON.stringify(proposal));
  if (proposal.byteLength > MAX_RECOVERY_BYTES) return false;

  const database = await openRecoveryDatabase(userId, indexedDBFactory);
  if (!database) return false;
  try {
    const write = database.transaction(PROPOSAL_STORE, 'readwrite').objectStore(PROPOSAL_STORE);
    await requestResult(write.put(proposal));
    const read = database.transaction(PROPOSAL_STORE, 'readonly').objectStore(PROPOSAL_STORE);
    const proposals = (await requestResult(read.getAll()))
      .filter((candidate) => candidate?.mindmapId === mindmapId)
      .sort((left, right) => String(right.updatedAt).localeCompare(String(left.updatedAt)));
    let retainedBytes = 0;
    const excess = proposals.filter((candidate, index) => {
      retainedBytes += Number(candidate.byteLength) || 0;
      return index >= MAX_CONFLICTED_NODES || retainedBytes > MAX_RECOVERY_BYTES;
    });
    if (excess.length) {
      const cleanup = database.transaction(PROPOSAL_STORE, 'readwrite').objectStore(PROPOSAL_STORE);
      await Promise.all(excess.map((candidate) => requestResult(cleanup.delete(candidate.id))));
    }
    return true;
  } finally {
    database.close();
  }
}

export async function readOfflineNodeProposals(
  userId,
  mindmapId,
  indexedDBFactory = typeof window !== 'undefined' ? window.indexedDB : null
) {
  if (!isUuid(userId) || !isUuid(mindmapId)) return new Map();
  const database = await openRecoveryDatabase(userId, indexedDBFactory);
  if (!database) return new Map();
  try {
    const store = database.transaction(PROPOSAL_STORE, 'readonly').objectStore(PROPOSAL_STORE);
    const proposals = await requestResult(store.getAll());
    return new Map(proposals
      .filter((proposal) => (
        proposal?.v === RECOVERY_VERSION
        && proposal.userId === userId
        && proposal.mindmapId === mindmapId
        && proposal.nodeId
        && proposal.node
      ))
      .sort((left, right) => String(left.updatedAt).localeCompare(String(right.updatedAt)))
      .slice(-MAX_CONFLICTED_NODES)
      .map((proposal) => [proposal.nodeId, proposal.node]));
  } finally {
    database.close();
  }
}

export async function clearOfflineNodeProposals(
  userId,
  mindmapId,
  indexedDBFactory = typeof window !== 'undefined' ? window.indexedDB : null
) {
  if (!isUuid(userId) || !isUuid(mindmapId)) return false;
  const database = await openRecoveryDatabase(userId, indexedDBFactory);
  if (!database) return false;
  try {
    const read = database.transaction(PROPOSAL_STORE, 'readonly').objectStore(PROPOSAL_STORE);
    const proposals = await requestResult(read.getAll());
    const matching = proposals.filter((proposal) => proposal?.mindmapId === mindmapId);
    if (matching.length) {
      const cleanup = database.transaction(PROPOSAL_STORE, 'readwrite').objectStore(PROPOSAL_STORE);
      await Promise.all(matching.map((proposal) => requestResult(cleanup.delete(proposal.id))));
    }
    return true;
  } finally {
    database.close();
  }
}

function requestResult(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Recovery storage request failed'));
  });
}

export async function saveOfflineRecovery(
  record,
  indexedDBFactory = typeof window !== 'undefined' ? window.indexedDB : null
) {
  if (
    !record?.id
    || record.storageEligible === false
    || !isUuid(record.userId)
    || !isUuid(record.mindmapId)
  ) return false;
  const database = await openRecoveryDatabase(record.userId, indexedDBFactory);
  if (!database) return false;
  try {
    const write = database.transaction(RECOVERY_STORE, 'readwrite').objectStore(RECOVERY_STORE);
    await requestResult(write.put(record));
    const read = database.transaction(RECOVERY_STORE, 'readonly').objectStore(RECOVERY_STORE);
    const records = await requestResult(read.getAll());
    const excess = records
      .filter((candidate) => candidate?.mindmapId === record.mindmapId)
      .sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)))
      .slice(MAX_RECOVERIES_PER_MAP);
    if (excess.length) {
      const cleanup = database.transaction(RECOVERY_STORE, 'readwrite').objectStore(RECOVERY_STORE);
      await Promise.all(excess.map((candidate) => requestResult(cleanup.delete(candidate.id))));
    }
    return true;
  } finally {
    database.close();
  }
}

export async function readLatestOfflineRecovery(
  userId,
  mindmapId,
  indexedDBFactory = typeof window !== 'undefined' ? window.indexedDB : null
) {
  if (!isUuid(userId) || !isUuid(mindmapId)) return null;
  const database = await openRecoveryDatabase(userId, indexedDBFactory);
  if (!database) return null;
  try {
    const store = database.transaction(RECOVERY_STORE, 'readonly').objectStore(RECOVERY_STORE);
    const records = await requestResult(store.getAll());
    return records
      .filter((record) => record?.mindmapId === mindmapId && record?.v === RECOVERY_VERSION)
      .sort((left, right) => String(right.createdAt).localeCompare(String(left.createdAt)))[0]
      || null;
  } finally {
    database.close();
  }
}

export function recoveryDownload(record) {
  const timestamp = String(record?.createdAt || '').replace(/[:.]/g, '-');
  return {
    filename: `mindmappi-recovery-${record?.mindmapId || 'map'}-${timestamp || 'local'}.json`,
    content: JSON.stringify(record, null, 2),
  };
}

export function downloadOfflineRecovery(record) {
  if (
    typeof document === 'undefined'
    || typeof URL?.createObjectURL !== 'function'
    || typeof Blob === 'undefined'
  ) return false;
  const { content, filename } = recoveryDownload(record);
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 100);
  return true;
}

export function recoveryText(record) {
  const nodes = record?.kind === 'whole-node-conflict'
    ? (record.conflicts || []).map((conflict) => conflict.localProposal)
    : record?.snapshot?.nodes || [];
  return nodes
    .map((node) => String(node?.text || '').trim())
    .filter(Boolean)
    .join('\n\n');
}

export const OFFLINE_RECOVERY_DATABASE_PREFIX = RECOVERY_DATABASE_PREFIX;
